import { extractExhaustiveKeys } from '@craft/lib/primitives';

import type * as types from './types';

export interface InteractablePointerEventProps {
  /**
   * Defines whether or not the interactable element is in a "disabled" state, which prevents any
   * interaction with the element via pointer events.
   *
   * @default false
   */
  readonly isDisabled?: boolean;
  /**
   * Defines whether or not the interactable element is in a "loading" state, which prevents any
   * interaction with the element via pointer events.
   *
   * @default false
   */
  readonly isLoading?: boolean;
  /**
   * Defines whether or not the interactable element is in a "locked" state, which prevents any
   * interaction with the element via pointer events.
   *
   * @default false
   */
  readonly isLocked?: boolean;
}

/**
 * Defines the props that can be used to customize the appearance of an interactable element
 * based on the {@link types.Interaction} state the element is currently exhibiting.
 */
export interface InteractableSxProps {
  /**
   * Defines the background color that should be applied to the element when it is in a focused
   * or focus-visible state and the focused indicator, {@link types.InteractionIndicator}, is or
   * includes {@link types.InteractionIndicator.Background}.
   *
   * The background color will default to a preset value if not provided.  If that is not desired,
   * this prop can be explicitly set to 'null' to prevent any background color from being applied
   * when the element is focused.
   *
   * @default 'action.hover'
   * (when the focus interaction is or includes {@link types.InteractionIndicator.Background})
   */
  readonly focusedBackgroundColor?: null | types.InteractionStyle['backgroundColor'];
  /**
   * Defines the border or outline color that should be applied to the element when it is in a
   * focused or focus-visible state and the focused indicator, {@link types.InteractionIndicator},
   * is or includes {@link types.InteractionIndicator.Border}.
   *
   * This value will be used both to define the 'borderColor'/'outlineColor' CSS property, as well
   * as the overall 'border'/'outline' CSS property.
   *
   * The border or outline color will default to a preset value if not provided.  If that is not
   * desired, this prop can be explicitly set to 'null' to prevent any border or outline from
   * being applied when the element is focused.
   *
   * @default 'secondary.main'
   * (when the focus indicator is or includes {@link types.InteractionIndicator.Border})
   */
  readonly focusedBorderColor?: null | types.InteractionStyle['borderColor'];
  /**
   * Defines the width, or thickness, of the border, or outline, that is applied to the element
   * when it is in a focused or focus-visible state and the focused indicator,
   * {@link types.InteractionIndicator}, is or includes {@link types.InteractionIndicator.Border}.
   *
   * This value will only have an effect if the focused indicator,
   * {@link types.InteractionIndicator}, is or includes {@link types.InteractionIndicator.Border},
   * and the 'focusedBorderColor' prop is not explicitly set to 'null'.
   *
   * @default 2px
   * (when the focus indicator is or includes {@link types.InteractionIndicator.Border})
   */
  readonly focusedBorderWidth?: types.InteractionStyle['borderWidth'];
  /**
   * Defines the style characteristics that the element should exhibit when in a focused state,
   * such as applying a background color, border, etc.
   *
   * This can be provided as a single indicator, {@link types.InteractionIndicator}, or as an array
   * of of behaviors to apply multiple styles when the element is focused.  If explicitly set to
   * null, no focus styles will be applied.
   *
   * @default [InteractionIndicator.Border]
   */
  readonly focusIndicator?: null | types.InteractionIndicators;
  /**
   * Defines the background color that should be applied to the element when it is in a hovered
   * state and the hovered indicator, {@link types.InteractionIndicator}, is or includes
   * {@link types.InteractionIndicator.Background}.
   *
   * The background color will default to a preset value if not provided.  If that is not desired,
   * this prop can be explicitly set to 'null' to prevent any background color from being applied
   * when the element is hovered.
   *
   * @default 'action.hover'
   * (when the hover indicator is or includes {@link types.InteractionIndicator.Background})
   */
  readonly hoveredBackgroundColor?: null | types.InteractionStyle['backgroundColor'];
  /**
   * Defines the border or outline color that should be applied to the element when it is in a
   * hovered state and the hovered indicator, {@link types.InteractionIndicator}, is or includes
   * {@link types.InteractionIndicator.Border}.
   *
   * This value will be used both to define the 'borderColor'/'outlineColor' CSS property, as well
   * as the overall 'border'/'outline' CSS property.
   *
   * The border or outline color will default to a preset value if not provided.  If that is not
   * desired, this prop can be explicitly set to 'null' to prevent any border or outline from
   * being applied when the element is hovered.
   *
   * @default 'action.active'
   * (when the hover indicator is or includes {@link types.InteractionIndicator.Border})
   */
  readonly hoveredBorderColor?: null | types.InteractionStyle['borderColor'];
  /**
   * Defines the width, or thickness, of the border, or outline, that is applied to the element
   * when it is in a hovered state and the hovered indicator, {@link types.InteractionIndicator},
   * is or includes {@link types.InteractionIndicator.Border}.
   *
   * This value will only have an effect if the hovered indicator,
   * {@link types.InteractionIndicator}, is or includes {@link types.InteractionIndicator.Border},
   * and the 'hoveredBorderColor' prop is not explicitly set to 'null'.
   *
   * @default 1px
   * (when the hover indicator is or includes {@link types.InteractionIndicator.Border})
   */
  readonly hoveredBorderWidth?: types.InteractionStyle['borderWidth'];
  /**
   * Defines the style characteristics that the element should exhibit when in a hovered state,
   * such as applying a background color, border, etc.
   *
   * This can be provided as a single indicator, {@link types.InteractionIndicator}, or as an array
   * of of behaviors to apply multiple styles when the element is hovered.  If explicitly set to
   * null, no hover styles will be applied.
   *
   * @default [types.InteractionIndicator.Background]
   */
  readonly hoverIndicator?: null | types.InteractionIndicators;
}

export const InteractionSxPropNames = extractExhaustiveKeys<InteractableSxProps>()({
  focusedBackgroundColor: true,
  focusedBorderColor: true,
  focusedBorderWidth: true,
  focusIndicator: true,
  hoveredBackgroundColor: true,
  hoveredBorderColor: true,
  hoveredBorderWidth: true,
  hoverIndicator: true,
});
